import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FORMATS = [
  "Company Strategy Day (1 company · 1 day)",
  "Selective Strategy Sprint (2–4 companies · 2 days)",
  "Portfolio Strategy Retreat (5–8 companies · 4 days)",
  "I'm not sure yet",
];

const TIMINGS = [
  "In the next 3 months",
  "In 3–9 months",
  "In 9+ months",
  "I'm not sure yet",
];

const EMPTY = {
  name: "",
  email: "",
  company: "",
  role: "",
  country: "",
  format: "",
  timing: "",
  description: "",
};

export default function ProgrammeEnquiryModal({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const isValid =
    form.name.trim() &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.company.trim() &&
    form.country.trim() &&
    form.format &&
    form.timing;

  const handleClose = (o) => {
    if (!o && !submitting) {
      onOpenChange(false);
      // reset shortly after close animation
      setTimeout(() => {
        setForm(EMPTY);
        setSubmitted(false);
      }, 300);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/programmes/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong — please try again or email marco@operon-hq.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-[#0B1735] border-[#243768] text-[#E9EEF9] max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="programme-enquiry-modal"
      >
        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#57C7E6]/10 border border-[#57C7E6]/40 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#57C7E6]" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white text-center">
                Thanks — enquiry received.
              </DialogTitle>
              <DialogDescription className="text-[#9DB0D6] text-center mt-2">
                We&apos;ll be in touch at <span className="text-white">{form.email}</span> within a couple of working days to design the right programme for you.
              </DialogDescription>
            </DialogHeader>
            <Button
              onClick={() => handleClose(false)}
              className="mt-6 bg-[#57C7E6] hover:bg-[#3DA9C9] text-[#060B1C] font-semibold rounded-full px-6"
              data-testid="btn-enquiry-close"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="text-[10px] font-mono text-[#57C7E6] tracking-[0.3em] uppercase mb-2">
                Design my programme
              </div>
              <DialogTitle className="text-2xl md:text-3xl font-bold text-white leading-tight">
                Tell us what you&apos;re thinking.
              </DialogTitle>
              <DialogDescription className="text-[#9DB0D6]">
                A few quick details and we&apos;ll get back to you personally to shape the right programme.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 mt-4" data-testid="programme-enquiry-form">
              {/* Format */}
              <div className="space-y-2">
                <Label className="text-[#E9EEF9]">Which format are you thinking of? <span className="text-[#57C7E6]">*</span></Label>
                <Select value={form.format} onValueChange={(v) => setForm((f) => ({ ...f, format: v }))}>
                  <SelectTrigger className="bg-[#0F1D40] border-[#243768] text-white" data-testid="select-format">
                    <SelectValue placeholder="Pick a format" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1D40] border-[#243768] text-white">
                    {FORMATS.map((f) => (
                      <SelectItem key={f} value={f} className="focus:bg-[#14264F] focus:text-white">
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Timing */}
              <div className="space-y-2">
                <Label className="text-[#E9EEF9]">Indicative timing <span className="text-[#57C7E6]">*</span></Label>
                <Select value={form.timing} onValueChange={(v) => setForm((f) => ({ ...f, timing: v }))}>
                  <SelectTrigger className="bg-[#0F1D40] border-[#243768] text-white" data-testid="select-timing">
                    <SelectValue placeholder="When are you looking to run it?" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1D40] border-[#243768] text-white">
                    {TIMINGS.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-[#14264F] focus:text-white">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-[#E9EEF9]">Any description or comment</Label>
                <Textarea
                  value={form.description}
                  onChange={set("description")}
                  placeholder="What you'd like to get out of the programme, therapeutic areas of focus, specific challenges, anything we should know…"
                  rows={4}
                  className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6] resize-none"
                  data-testid="input-description"
                />
              </div>

              <div className="pt-2 border-t border-[#243768]" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#E9EEF9]">Your name <span className="text-[#57C7E6]">*</span></Label>
                  <Input
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Alex Smith"
                    className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6]"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#E9EEF9]">Role (optional)</Label>
                  <Input
                    value={form.role}
                    onChange={set("role")}
                    placeholder="Programme Director"
                    className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6]"
                    data-testid="input-role"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#E9EEF9]">Email address <span className="text-[#57C7E6]">*</span></Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="alex@yourcompany.com"
                  className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6]"
                  data-testid="input-email"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#E9EEF9]">Company / Organisation <span className="text-[#57C7E6]">*</span></Label>
                  <Input
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Acme Accelerator"
                    className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6]"
                    data-testid="input-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#E9EEF9]">Country <span className="text-[#57C7E6]">*</span></Label>
                  <Input
                    value={form.country}
                    onChange={set("country")}
                    placeholder="Germany"
                    className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6]"
                    data-testid="input-country"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  className="border-[#243768] bg-transparent text-[#9DB0D6] hover:bg-[#14264F] hover:text-white"
                  disabled={submitting}
                  data-testid="btn-enquiry-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || submitting}
                  className="bg-[#57C7E6] hover:bg-[#3DA9C9] text-[#060B1C] font-semibold rounded-full px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-enquiry-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send enquiry
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
